import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {View, TouchableOpacity, Dimensions, Image} from 'react-native';
import Box from '@/Atoms/Box';
import {styles} from './styles';
import CCColors from '@/Utils/CCColors';
import MaterialIcon from 'react-native-vector-icons/MaterialCommunityIcons';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import {FontSizes, WINDOW_WIDTH} from '@/Utils/Dimensions';
import Modal from 'react-native-modal';
import Ionicons from 'react-native-vector-icons/Ionicons';
import streakDetailsBg from '@/Assets/Dashboard/StreakDropdownBg/streak_dropdown_bg.svg';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {useNavigation, useNavigationState} from '@react-navigation/native';
import {ScreenName} from '@/navigators/StackConstants';
import {subtractDaysFromDate} from '@/Utils/CommonUtils';
import Environment from '../../../../../environment';
import {useEngagementData} from '@/Screens/Leaderboard/useEngagementData';
import MilestoneBanner from '@/Screens/Leaderboard/components/MilestoneBanner';
import {
  getNudgeSlot,
  buildNudgeCopy,
  shouldShowNudge,
  getShownSlots,
  markNudgeShown,
  NudgeSlot,
} from '@/Screens/Leaderboard/streakNudges';
import CaissaNudge from './CaissaNudge';

const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Header nudge: a Caissa speech-bubble card below the flame when today's
// streak activity isn't done yet (CaissaNudge.tsx owns the presentation).
// Copy/cadence come from the 4-slot time-of-day ladder in streakNudges.ts
// (shared with mobile) — each slot shows at most once per streak-day,
// tracked via streak_nudges_shown:<serverDate>.
const NUDGE_VISIBLE_MS = 10000;
// Re-check the UTC-hour slot bucket periodically while the header stays
// mounted, in addition to the mount/Dashboard-focus triggers below.
const NUDGE_RECHECK_INTERVAL_MS = 15 * 60 * 1000;

const getWeekDays = (lastStreakDate, count) => {
  const today = new Date();
  // count = 0;
  // lastStreakDate = null;
  lastStreakDate = new Date(lastStreakDate);
  const lastStreakDateIndex = lastStreakDate.getDay();
  const todayIndex = today.getDay(); // 0 for Sunday, 1 for Monday, etc.
  // If no startDayInWeek is provided, default to today's day
  if (lastStreakDate === null) {
    lastStreakDate = todayIndex;
  }
  // l
  let streakStartDate = subtractDaysFromDate(lastStreakDate, count);
  let startIndex = 0;
  if (lastStreakDateIndex === todayIndex) {
    streakStartDate = subtractDaysFromDate(lastStreakDate, count - 1);
    startIndex = streakStartDate.getDay();
  } else {
    startIndex = streakStartDate.getDay() + 1;
  }
  // Adjust the day to be Monday = 0, Sunday = 6
  // debugger;
  // startIndex = (startDayInWeek + 6) % 7;

  // Rotate the days of the week based on the start day
  const rotatedDays = [
    ...daysOfWeek.slice(startIndex),
    ...daysOfWeek.slice(0, startIndex),
  ];

  return rotatedDays.map((day, index) => {
    const actualDayIndex = (startIndex + index) % 7;
    // Highlight the first day in red (this is the day the streak started)
    if (index === 0 && count > 0) {
      return {title: day, color: CCColors?.Red};
    }

    // If today and the streak is still active, mark today as White
    else if (
      actualDayIndex === todayIndex &&
      lastStreakDateIndex !== todayIndex
    ) {
      return {title: day, color: CCColors?.White};
    }

    // For all streak days, mark them as Aqua
    else if (index < count) {
      return {title: day, color: CCColors?.Primary?.Aqua};
    }

    // All other days will be BrownShade2
    return {title: day, color: CCColors?.BrownShade2};
  });
};

function Streak() {
  const {streakConfig, streakDetailsSummary} = useLoggedInUserStore();
  const {isMobileOrTab} = useMediaQuery();
  // count 0 until real data lands — a non-zero placeholder here briefly
  // showed every fresh account a fake streak.
  const [streakDays, setStreakDays] = useState({count: 0, startDayInWeek: 7});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [weekDays, setWeekDays] = useState([]);
  const navigation = useNavigation();
  // Milestone progress banner ("N more days → eligible for the Weekly Draw")
  // — falls back gracefully (banner hidden) while engagement is loading,
  // gated off, or the summary call fails.
  const {summary: engagement, presentation: engPresentation} =
    useEngagementData();
  const engagementEnabled = engagement.pageEnabled;
  const handleGo = (title: string) => {
    if (title?.toLowerCase()?.includes('win')) {
      navigation.navigate(ScreenName?.PlayChess);
    } else if (title?.toLowerCase()?.includes('positions')) {
      navigation.navigate(ScreenName?.Puzzles);
    } else if (title?.toLowerCase()?.includes('games')) {
      navigation.navigate(ScreenName?.Games);
    } else if (title?.toLowerCase()?.includes('calculation')) {
      navigation.navigate(ScreenName?.Visualisation, {selected: 'calculation'});
    }
  };
  const openStreakAndRewards = () => {
    setIsModalVisible(false);
    navigation.navigate(ScreenName?.Leaderboard);
  };
  useEffect(() => {
    setWeekDays(getWeekDays(streakDays.startDayInWeek, streakDays.count));
  }, [streakDays]);

  // The modal's daily-goal rows follow the engagement summary's criteria
  // (same live query as the flame count: invalidated on game end + 60s poll);
  // the legacy streakConfig store — which PlayChess never refreshes — is only
  // a fallback, so progress like "1/1 won" updates without a reload.
  const criteriaSource = useMemo(() => {
    if (engagementEnabled && engagement.streak.criteria?.length) {
      return engagement.streak.criteria.map(c => ({
        id: c.id,
        name: c.name,
        description: c.description,
        target_count: c.targetCount,
        user_count: c.userCount,
      }));
    }
    return streakConfig;
  }, [engagementEnabled, engagement.streak.criteria, streakConfig]);

  const streakDetails = useMemo(() => {
    return criteriaSource?.map(streakItem => {
      // Casual segment's daily requirement is "Win 1 game" (LOCKED copy) —
      // the CSOC/learning criteria keep their existing "Solve X"/"Review X"
      // titles.
      const isWinCriterion = streakItem?.name?.toLowerCase()?.includes('win');
      const title = isWinCriterion
        ? 'Win 1 game'
        : streakItem?.name?.includes('Review')
        ? streakItem?.name
        : `Solve ${streakItem?.name}`;
      return {
        id: streakItem?.id,
        title,
        description: streakItem?.description,
        subTitle: isWinCriterion
          ? `${
              streakItem?.user_count >= streakItem?.target_count
                ? streakItem?.target_count
                : streakItem?.user_count
            }/${streakItem?.target_count} won`
          : `${
              streakItem?.user_count >= streakItem?.target_count
                ? streakItem?.target_count
                : streakItem?.user_count
            }/${streakItem?.target_count} ${
              streakItem?.name?.includes('Review') ? 'review' : 'solved'
            }`,
        isCompleted: streakItem?.user_count >= streakItem?.target_count,
      };
    });
  }, [criteriaSource]);

  useEffect(() => {
    if (streakDetailsSummary) {
      setStreakDays({
        count: streakDetailsSummary?.streak_count,
        startDayInWeek: streakDetailsSummary?.last_streak_date || 0,
      });
    }
  }, [streakDetailsSummary]);

  // The flame count follows the engagement summary whenever the feature is
  // live: that query is invalidated on game end (ResultComponent) and polls
  // every 60s, so wins reflect within seconds. The legacy zustand store above
  // only updates on certain screens' fetches (PlayChess is not one of them),
  // which left the header stale after a win until a full reload. Runs after
  // the legacy effect, so when both fire the summary value wins.
  useEffect(() => {
    if (engagementEnabled) {
      setStreakDays({
        count: engagement.streak.streakCount,
        startDayInWeek: engagement.streak.lastStreakDate || 0,
      });
    }
  }, [
    engagementEnabled,
    engagement.streak.streakCount,
    engagement.streak.lastStreakDate,
  ]);

  const toggleModal = e => {
    if (e) e.preventDefault();
    setShowNudge(false);
    setIsModalVisible(!isModalVisible);
  };

  // ---------------------------------------------------------------------
  // Header nudge tooltip: shown at most once per streak-day per slot.
  // Triggered on initial mount, Dashboard focus, and a 15-min re-check
  // while mounted (so the tooltip can pick up a later slot in-session).
  // ---------------------------------------------------------------------
  const [showNudge, setShowNudge] = useState(false);
  const [nudgeText, setNudgeText] = useState('');
  const [nudgeSlot, setNudgeSlot] = useState<NudgeSlot>('fresh');
  const activeRouteName = useNavigationState(
    state => state?.routes?.[state?.index]?.name,
  );
  // Mount + Dashboard-focus can both fire in the same tick (e.g. landing
  // straight on Dashboard) and each awaits getShownSlots before writing — an
  // in-flight guard collapses that into a single check instead of racing
  // redundant reads/writes.
  const nudgeCheckInFlightRef = useRef(false);

  const maybeShowNudge = useCallback(async () => {
    if (nudgeCheckInFlightRef.current) {
      return;
    }
    nudgeCheckInFlightRef.current = true;
    try {
      // Dev/QA preview: ENGAGEMENT_NUDGE_FORCE_SLOT in env.local.js forces a
      // slot's nudge on every load, bypassing today-complete and shown-state
      // (absent from committed env files, so inert everywhere else).
      const forcedSlot = (
        Environment as {ENGAGEMENT_NUDGE_FORCE_SLOT?: string}
      )?.ENGAGEMENT_NUDGE_FORCE_SLOT;
      if (forcedSlot && engagementEnabled) {
        const nextShortForced =
          engagement?.nextMilestone?.label ??
          engagement?.nextMilestone?.prizeShort;
        setNudgeSlot(forcedSlot as NudgeSlot);
        setNudgeText(
          buildNudgeCopy(
            forcedSlot as Parameters<typeof buildNudgeCopy>[0],
            engagement?.streak?.streakCount ?? 0,
            nextShortForced,
          ),
        );
        setShowNudge(true);
        return;
      }
      if (!engagementEnabled || engagement?.streak?.todayComplete) {
        return;
      }
      const slot = getNudgeSlot(new Date().getUTCHours());
      const shownSlots = await getShownSlots(engagement?.serverDate);
      if (
        !shouldShowNudge({
          slot,
          shownSlots,
          pageEnabled: engagementEnabled,
          todayComplete: engagement?.streak?.todayComplete,
        })
      ) {
        return;
      }
      const nextShort =
        engagement?.nextMilestone?.label ??
        engagement?.nextMilestone?.prizeShort;
      setNudgeSlot(slot);
      setNudgeText(
        buildNudgeCopy(slot, engagement?.streak?.streakCount ?? 0, nextShort),
      );
      await markNudgeShown(slot, engagement?.serverDate);
      setShowNudge(true);
    } finally {
      nudgeCheckInFlightRef.current = false;
    }
  }, [
    engagementEnabled,
    engagement?.streak?.todayComplete,
    engagement?.streak?.streakCount,
    engagement?.serverDate,
    engagement?.nextMilestone?.label,
    engagement?.nextMilestone?.prizeShort,
  ]);

  useEffect(() => {
    // Initial-mount trigger, and re-runs once the engagement summary
    // finishes loading (maybeShowNudge is a no-op until then anyway).
    maybeShowNudge();
  }, [maybeShowNudge]);

  useEffect(() => {
    if (activeRouteName === ScreenName?.Dashboard) {
      maybeShowNudge();
    }
  }, [activeRouteName, maybeShowNudge]);

  useEffect(() => {
    const interval = setInterval(maybeShowNudge, NUDGE_RECHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [maybeShowNudge]);

  useEffect(() => {
    if (!showNudge) {
      return undefined;
    }
    const timer = setTimeout(() => setShowNudge(false), NUDGE_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [showNudge]);

  return (
    <Box
      style={[
        styles.streakContainer,
        isMobileOrTab && styles.streakContainerMobile,
      ]}>
      <TouchableOpacity onPress={toggleModal}>
        <Box
          style={[
            styles.streakWrapper,
            isMobileOrTab && styles.streakWrapperMobile,
          ]}>
          <MaterialIcon
            name={'lightning-bolt-circle'}
            size={21}
            color={CCColors?.Red}
          />
          {!isMobileOrTab ? (
            <>
              <Spacer spacing={5} horizontal />
              <CCText
                fontSize={FontSizes[12]}
                fontWeight="medium"
                style={styles.textWrapper}>
                {streakDays.count} days streak
              </CCText>
            </>
          ) : (
            <>
              <Spacer spacing={5} horizontal />
              <CCText fontWeight="medium" style={styles.textWrapper}>
                {streakDays.count}
              </CCText>
            </>
          )}
        </Box>
      </TouchableOpacity>
      {showNudge ? (
        <CaissaNudge
          slot={nudgeSlot}
          text={nudgeText}
          onPressBody={toggleModal}
          onDismiss={() => setShowNudge(false)}
          onPlay={() => {
            setShowNudge(false);
            navigation.navigate(ScreenName?.PlayChess);
          }}
        />
      ) : null}
      <Modal
        isVisible={isModalVisible}
        animationIn={'zoomInDown'}
        animationOut={'zoomOutDown'}
        onBackdropPress={toggleModal}
        backdropOpacity={0}
        style={[
          styles.modal,
          {
            top: 73,
            right: WINDOW_WIDTH / 5,
          },
        ]}
        useNativeDriver>
        <Box style={styles.streakDetailsContainer}>
          <Box style={styles.streakDetailsTop}>
            <Image style={styles.bgImage} source={streakDetailsBg} />
            <View style={styles.streakDetailsContainerTitle}>
              <MaterialIcon
                name={'lightning-bolt'}
                size={30}
                color={CCColors?.Red}
              />
              <CCText
                color={CCColors?.White}
                fontWeight="medium"
                style={styles.textWrapper}>
                {streakDays.count}
              </CCText>
              <Spacer spacing={2} horizontal />
              <CCText color={CCColors?.White} style={styles.textWrapper}>
                days streak
              </CCText>
            </View>
            <View style={styles.weekDaysContainer}>
              {weekDays?.map((day, index) => (
                <View
                  key={index}
                  style={[styles.weekDay, {backgroundColor: day?.color}]}>
                  <CCText
                    color={
                      day.color?.includes('FFFFFF')
                        ? CCColors?.TextColor?.Primary
                        : CCColors?.White
                    }>
                    {day.title}
                  </CCText>
                </View>
              ))}
            </View>
            <CCText color={CCColors?.White} style={styles.textWrapper}>
              Complete the below activities to continue your streak{' '}
            </CCText>
          </Box>
          <Box style={styles.streakDetailsBottom}>
            {streakDetails?.map(eachStreak => (
              <React.Fragment key={eachStreak.id}>
                <View style={styles.eachStreakDetailsWrapper}>
                  <View>
                    <CCText
                      fontSize={FontSizes[14]}
                      {...(eachStreak.isCompleted && {
                        style: styles.strikethroughText,
                      })}>
                      {eachStreak.title}
                    </CCText>
                    <CCText
                      fontSize={FontSizes[12]}
                      color={CCColors?.TextColor?.Grey}>
                      {eachStreak.subTitle}
                    </CCText>
                  </View>
                  {eachStreak.isCompleted ? (
                    <Ionicons
                      name={'checkmark-circle'}
                      size={30}
                      color={CCColors?.Primary.Aqua}
                    />
                  ) : (
                    <Box
                      style={styles.goButton}
                      onPress={() => handleGo(eachStreak?.title)}>
                      <CCText fontWeight="semibold">GO</CCText>
                    </Box>
                  )}
                </View>
                <Spacer spacing={8} />
              </React.Fragment>
            ))}
            {engagementEnabled ? (
              <>
                <Spacer spacing={4} />
                <MilestoneBanner
                  variant={engPresentation.variant}
                  text={engPresentation.bannerText}
                  progressPct={engPresentation.progressPct}
                  progressLeftLabel={engPresentation.progressLeftLabel}
                  progressRightLabel={engPresentation.progressRightLabel}
                  compact
                />
                <Spacer spacing={10} />
                <Box
                  style={styles.openRewardsButton}
                  onPress={openStreakAndRewards}>
                  <CCText fontWeight="bold" color={CCColors?.Primary?.Brown}>
                    Open Streak & Rewards →
                  </CCText>
                </Box>
              </>
            ) : null}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default Streak;
